import { supabase, getSchema } from '@/lib/supabase';
import {
  mapCommunicationFromDb,
  mapCommunicationViewFromDb,
  type ClientCommunication,
  type ClientCommunicationView,
  type UploadCommunicationParams,
  type UpdateCommunicationParams,
  type CommunicationFilters,
} from '../types';

/**
 * Get the storage bucket name based on environment
 */
function getBucketName(): string {
  const schema = getSchema();
  return schema === 'testing' ? 'client-communications-testing' : 'client-communications';
}

/**
 * Build the storage path for a communication file
 * Format: {business_id}/{project_id}/{communication_id}.{ext}
 */
function buildStoragePath(
  businessId: string,
  projectId: string,
  communicationId: string,
  fileName: string
): string {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  return `${businessId}/${projectId}/${communicationId}.${extension}`;
}

/**
 * Upload a communication file to storage and create database record
 */
export async function uploadCommunication(
  params: UploadCommunicationParams
): Promise<ClientCommunication> {
  const {
    file,
    businessId,
    projectId,
    uploadedBy,
    communicationType,
    tags = [],
    notes,
  } = params;

  const bucketName = getBucketName();
  const communicationId = crypto.randomUUID();
  const filePath = buildStoragePath(businessId, projectId, communicationId, file.name);

  // 1. Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading communication file:', uploadError);
    throw uploadError;
  }

  // 2. Create database record
  const communicationRecord = {
    id: communicationId,
    business_id: businessId,
    project_id: projectId,
    uploaded_by: uploadedBy,
    file_name: file.name,
    file_path: filePath,
    file_size_bytes: file.size,
    mime_type: file.type || null,
    communication_type: communicationType,
    tags: tags,
    notes: notes || null,
  };

  const { data, error: insertError } = await supabase
    .from('client_communications')
    .insert(communicationRecord)
    .select()
    .single();

  if (insertError) {
    // Try to clean up uploaded file
    await supabase.storage.from(bucketName).remove([filePath]);
    console.error('Error creating communication record:', insertError);
    throw insertError;
  }

  return mapCommunicationFromDb(data);
}

/**
 * Get communications for a specific project
 */
export async function getProjectCommunications(
  projectId: string
): Promise<ClientCommunicationView[]> {
  const { data, error } = await supabase
    .from('v_client_communications')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching project communications:', error);
    throw error;
  }

  return (data || []).map(mapCommunicationViewFromDb);
}

/**
 * Get communications with optional filters
 */
export async function getCommunications(
  businessId: string,
  filters?: CommunicationFilters
): Promise<ClientCommunicationView[]> {
  let query = supabase
    .from('v_client_communications')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (filters?.projectId) {
    query = query.eq('project_id', filters.projectId);
  }

  if (filters?.communicationType) {
    query = query.eq('communication_type', filters.communicationType);
  }

  if (filters?.tags && filters.tags.length > 0) {
    query = query.overlaps('tags', filters.tags);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching communications:', error);
    throw error;
  }

  return (data || []).map(mapCommunicationViewFromDb);
}

/**
 * Get a single communication by ID
 */
export async function getCommunicationById(
  id: string
): Promise<ClientCommunicationView | null> {
  const { data, error } = await supabase
    .from('v_client_communications')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching communication:', error);
    throw error;
  }

  return data ? mapCommunicationViewFromDb(data) : null;
}

/**
 * Update communication metadata (type, tags, notes)
 */
export async function updateCommunication(
  params: UpdateCommunicationParams
): Promise<ClientCommunication> {
  const { id, ...updates } = params;

  // Build the update object with snake_case keys
  const updateData: Record<string, unknown> = {};
  if (updates.communicationType !== undefined) {
    updateData.communication_type = updates.communicationType;
  }
  if (updates.tags !== undefined) {
    updateData.tags = updates.tags;
  }
  if (updates.notes !== undefined) {
    updateData.notes = updates.notes;
  }

  const { data, error } = await supabase
    .from('client_communications')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating communication:', error);
    throw error;
  }

  return mapCommunicationFromDb(data);
}

/**
 * Delete a communication (removes from storage and database)
 */
export async function deleteCommunication(id: string, filePath: string): Promise<void> {
  const bucketName = getBucketName();

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (storageError) {
    console.error('Error deleting communication from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('client_communications')
    .delete()
    .eq('id', id);

  if (dbError) {
    console.error('Error deleting communication from database:', dbError);
    throw dbError;
  }
}

/**
 * Bulk delete communications
 */
export async function bulkDeleteCommunications(
  items: { id: string; filePath: string }[]
): Promise<void> {
  const bucketName = getBucketName();
  const filePaths = items.map((item) => item.filePath);
  const ids = items.map((item) => item.id);

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove(filePaths);

  if (storageError) {
    console.error('Error deleting communications from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('client_communications')
    .delete()
    .in('id', ids);

  if (dbError) {
    console.error('Error deleting communications from database:', dbError);
    throw dbError;
  }
}

/**
 * Get a signed URL for viewing/downloading a communication file
 */
export async function getCommunicationUrl(
  filePath: string,
  expiresIn: number = 3600
): Promise<string> {
  const bucketName = getBucketName();

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Error creating signed URL:', error);
    throw error;
  }

  return data.signedUrl;
}

/**
 * Get signed URLs for multiple files (batch)
 */
export async function getCommunicationUrls(
  filePaths: string[],
  expiresIn: number = 3600
): Promise<Map<string, string>> {
  const bucketName = getBucketName();
  const urlMap = new Map<string, string>();

  // Supabase doesn't have batch signed URL, so we do them in parallel
  const results = await Promise.allSettled(
    filePaths.map(async (filePath) => {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(filePath, expiresIn);

      if (error) throw error;
      return { filePath, url: data.signedUrl };
    })
  );

  for (const result of results) {
    if (result.status === 'fulfilled') {
      urlMap.set(result.value.filePath, result.value.url);
    }
  }

  return urlMap;
}
